// combatpro/runtime/EncounterGeneratorCard.js
//
// Combat PRO - Random / Wave generator control card (spec 8, 13, 19, 25). Preset
// chips over dropdowns where practical; a compact, touch-friendly control set that
// simply populates the pure-engine generator inputs. Reused by the dashboard
// sections and the dedicated pages. No optional chaining. No em dash.

import React from 'react';
import {
  DIFFICULTIES, DIFFICULTY_LABELS, CREATURE_TYPES, CREATURE_TYPE_LABELS,
  COMPOSITIONS, COMPOSITION_LABELS, SOURCES, SOURCE_LABELS, ENVIRONMENTS, ENVIRONMENT_LABELS,
  WAVE_COUNTS, WAVE_STYLES, WAVE_STYLE_LABELS, RANDOM_QUICK_PRESETS, WAVE_QUICK_PRESETS, MONSTER_CAP
} from '../encounter/encounterGenEngine';

function ChipRow(props) {
  return (
    <div className="cp-gen-chiprow">
      {props.options.map(function (opt) {
        var active = String(props.value) === String(opt.value);
        return (
          <button
            type="button"
            key={opt.value}
            className={'cp-gen-chip' + (active ? ' is-on' : '')}
            onClick={function () { props.onPick(opt.value); }}
          >{opt.label}</button>
        );
      })}
    </div>
  );
}

function levelOptions() {
  var out = [];
  for (var i = 1; i <= 20; i++) { out.push(i); }
  return out;
}

function sourceOptions(gen) {
  return SOURCES.filter(function (sv) {
    if (sv === 'mine') { return gen.hasCustom; }
    if (sv === 'favourites') { return gen.hasFavourites; }
    return true;
  }).map(function (sv) { return { value: sv, label: SOURCE_LABELS[sv] }; });
}

function EncounterGeneratorCard(props) {
  var gen = props.gen;
  var s = gen.settings;
  var isWave = gen.isWave;
  var slots = Array.isArray(props.slots) ? props.slots : [];
  var target = props.target;
  var setTarget = props.setTarget;

  var diffOpts = DIFFICULTIES.map(function (d) { return { value: d, label: DIFFICULTY_LABELS[d] }; });
  var typeOpts = CREATURE_TYPES.map(function (t) { return { value: t, label: CREATURE_TYPE_LABELS[t] }; });
  var compOpts = COMPOSITIONS.map(function (c) { return { value: c, label: COMPOSITION_LABELS[c] }; });
  var styleOpts = WAVE_STYLES.map(function (w) { return { value: w, label: WAVE_STYLE_LABELS[w] }; });
  var waveOpts = WAVE_COUNTS.map(function (w) { return { value: w, label: (w === 'random' ? 'Random' : w) }; });
  var presets = isWave ? WAVE_QUICK_PRESETS : RANDOM_QUICK_PRESETS;

  return (
    <div className="cp-gen-card">
      <div className="cp-gen-card-head">
        <span className="cp-gen-card-title">{isWave ? 'Wave Encounter Generator' : 'Random Encounter Generator'}</span>
        <span className="cp-gen-card-sub">{isWave ? 'A complete multi-stage battle from a few presets.' : 'An appropriate surprise fight in seconds.'}</span>
      </div>

      <div className="cp-gen-presets">
        <span className="cp-gen-presets-label">Quick presets</span>
        <div className="cp-gen-chiprow">
          {presets.map(function (p) {
            return <button type="button" key={p.id} className="cp-gen-chip cp-gen-chip-preset" onClick={function () { gen.applyPreset(p.id); }}>{p.label}</button>;
          })}
        </div>
      </div>

      <div className="cp-gen-grid">
        <div className="cp-gen-field">
          <label className="cp-gen-label">Encounter Level</label>
          <div className="cp-gen-inline">
            <select className="cp-gen-select" value={s.level} disabled={s.useParty} onChange={function (e) { gen.setField('level', parseInt(e.target.value, 10)); }}>
              {levelOptions().map(function (lv) { return <option key={lv} value={lv}>{'Level ' + lv}</option>; })}
            </select>
            <label className="cp-gen-check">
              <input type="checkbox" checked={s.useParty} onChange={function (e) { gen.setField('useParty', e.target.checked); }} />
              <span>Use current party</span>
            </label>
          </div>
        </div>

        <div className="cp-gen-field">
          <label className="cp-gen-label">Difficulty</label>
          <ChipRow options={diffOpts} value={s.difficulty} onPick={function (v) { gen.setField('difficulty', v); }} />
        </div>

        {isWave ? (
          <div className="cp-gen-field">
            <label className="cp-gen-label">{'Total Monsters (1-' + MONSTER_CAP + ')'}</label>
            <input type="number" min="1" max={MONSTER_CAP} className="cp-gen-select" value={s.totalMonsters} onChange={function (e) { gen.setField('totalMonsters', parseInt(e.target.value, 10) || 1); }} />
          </div>
        ) : (
          <div className="cp-gen-field">
            <label className="cp-gen-label">{'Monster Count (1-' + MONSTER_CAP + ')'}</label>
            <input type="number" min="1" max={MONSTER_CAP} className="cp-gen-select" value={s.count} onChange={function (e) { gen.setField('count', parseInt(e.target.value, 10) || 1); }} />
          </div>
        )}

        {isWave ? (
          <div className="cp-gen-field">
            <label className="cp-gen-label">Number of Waves</label>
            <ChipRow options={waveOpts} value={s.waves} onPick={function (v) { gen.setField('waves', v); }} />
          </div>
        ) : null}

        <div className="cp-gen-field cp-gen-field-wide">
          <label className="cp-gen-label">Creature Type</label>
          <ChipRow options={typeOpts} value={s.creatureType} onPick={function (v) { gen.setField('creatureType', v); }} />
        </div>

        {isWave ? (
          <div className="cp-gen-field cp-gen-field-wide">
            <label className="cp-gen-label">Wave Style</label>
            <ChipRow options={styleOpts} value={s.style} onPick={function (v) { gen.setField('style', v); }} />
          </div>
        ) : (
          <div className="cp-gen-field cp-gen-field-wide">
            <label className="cp-gen-label">Composition</label>
            <ChipRow options={compOpts} value={s.composition} onPick={function (v) { gen.setField('composition', v); }} />
          </div>
        )}

        <div className="cp-gen-field">
          <label className="cp-gen-label">Source</label>
          <ChipRow options={sourceOptions(gen)} value={s.source} onPick={function (v) { gen.setField('source', v); }} />
        </div>

        <div className="cp-gen-field">
          <label className="cp-gen-label">Environment</label>
          <ChipRow options={ENVIRONMENTS.map(function (ev) { return { value: ev, label: ENVIRONMENT_LABELS[ev] }; })} value={s.environment} onPick={function (v) { gen.setField('environment', v); }} />
        </div>
      </div>

      <div className="cp-gen-actions">
        <label className="cp-gen-label cp-gen-target-label">Generate into</label>
        <select className="cp-gen-select cp-gen-target" value={target} onChange={function (e) { setTarget(parseInt(e.target.value, 10)); }}>
          {slots.map(function (slot, i) {
            var nm = (slot && slot.meta && slot.meta.name) ? slot.meta.name : ((isWave ? 'Wave Encounter ' : 'Random Encounter ') + (i + 1));
            var st = (slot && slot.gen && slot.gen.state) ? slot.gen.state : 'empty';
            return <option key={i} value={i}>{(i + 1) + ' \u2014 ' + nm + ' (' + st + ')'}</option>;
          })}
        </select>
        <button type="button" className="cp-gen-generate" onClick={function () { gen.generate(target); }}>{'\u2699\uFE0F Generate'}</button>
      </div>

      {gen.failure ? (
        <div className="cp-gen-failure" role="alert">{gen.failure}</div>
      ) : null}
    </div>
  );
}

export default EncounterGeneratorCard;
